import React from 'react';

/**
 * UnifiedCard — the canonical surface element for the MESP platform.
 *
 * Renders a card that automatically adapts to the active theme
 * (.mesp-theme-dark or .mesp-theme-light) via CSS variables defined in
 * tokens.css. Pages do NOT hardcode background colors anymore.
 *
 * Props:
 *  - variant: 'default' | 'elevated' | 'sunken' | 'outlined'
 *  - padding: 'none' | 'sm' | 'md' | 'lg'
 *  - interactive: boolean — adds hover lift effect
 *  - accent: boolean — adds gold top accent line
 *  - className: extra Tailwind classes (use sparingly)
 */
export const UnifiedCard = React.forwardRef(function UnifiedCard(
  {
    variant = 'default',
    padding = 'md',
    interactive = false,
    accent = false,
    className = '',
    style,
    children,
    testId,
    ...rest
  },
  ref
) {
  const paddings = {
    none: '0',
    sm: 'var(--mesp-space-4)',
    md: 'var(--mesp-space-6)',
    lg: 'var(--mesp-space-8)',
  };

  const surface = {
    default:  'var(--mesp-bg-elevated)',
    elevated: 'var(--mesp-bg-elevated)',
    sunken:   'var(--mesp-bg-sunken)',
    outlined: 'transparent',
  }[variant];

  const cardStyle = {
    background: surface,
    border: '1px solid var(--mesp-border)',
    borderRadius: 'var(--mesp-radius-lg)',
    boxShadow: variant === 'outlined' ? 'none' : 'var(--mesp-shadow-card)',
    color: 'var(--mesp-text)',
    padding: paddings[padding],
    transition: `transform var(--mesp-trans-base), border-color var(--mesp-trans-base), box-shadow var(--mesp-trans-base)`,
    position: 'relative',
    overflow: 'hidden',
    ...style,
  };

  const hoverProps = interactive
    ? {
        onMouseEnter: (e) => {
          e.currentTarget.style.borderColor = 'var(--mesp-border-strong)';
          e.currentTarget.style.transform = 'translateY(-2px)';
        },
        onMouseLeave: (e) => {
          e.currentTarget.style.borderColor = 'var(--mesp-border)';
          e.currentTarget.style.transform = 'translateY(0)';
        },
      }
    : {};

  return (
    <div
      ref={ref}
      data-testid={testId}
      className={`mesp-ui ${className}`}
      style={cardStyle}
      {...hoverProps}
      {...rest}
    >
      {accent && (
        <div
          aria-hidden
          style={{
            position: 'absolute',
            top: 0, left: 0, right: 0,
            height: 3,
            background: 'linear-gradient(90deg, #B5901C, var(--mesp-gold), #F3E5AB)',
          }}
        />
      )}
      {children}
    </div>
  );
});

export const UnifiedCardHeader = ({ title, subtitle, action, className = '', testId }) => (
  <div
    className={`flex items-start justify-between gap-4 ${className}`}
    style={{ marginBottom: 'var(--mesp-space-4)' }}
    data-testid={testId}
  >
    <div className="min-w-0">
      {title && (
        <h3
          style={{
            fontSize: 'var(--mesp-font-h3)',
            fontWeight: 'var(--mesp-font-weight-semibold)',
            color: 'var(--mesp-text)',
            margin: 0,
            lineHeight: 1.3,
          }}
        >
          {title}
        </h3>
      )}
      {subtitle && (
        <p
          style={{
            fontSize: 'var(--mesp-font-sm)',
            color: 'var(--mesp-text-muted)',
            marginTop: 'var(--mesp-space-1)',
            margin: '4px 0 0 0',
          }}
        >
          {subtitle}
        </p>
      )}
    </div>
    {action && <div className="flex-shrink-0">{action}</div>}
  </div>
);

export const UnifiedCardFooter = ({ children, className = '', testId }) => (
  <div
    className={className}
    style={{
      marginTop: 'var(--mesp-space-5)',
      paddingTop: 'var(--mesp-space-4)',
      borderTop: '1px solid var(--mesp-divider)',
    }}
    data-testid={testId}
  >
    {children}
  </div>
);

export default UnifiedCard;
